import { Skeleton } from '@/components/atoms';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { GuestDetailsLayout } from '../../constants/guestLayout';
import { GuestPropertyCardSkeleton } from './GuestPropertyCardSkeleton';

export interface GuestDetailsScreenSkeletonProps {
  readonly layout: GuestDetailsLayout;
}

/** Guest unit-details page skeleton: hero + about + explore sidebar. */
export function GuestDetailsScreenSkeleton({
  layout,
}: Readonly<GuestDetailsScreenSkeletonProps>) {
  const exploreCount = layout.isCompact ? 2 : 3;

  return (
    <View
      style={[
        styles.container,
        layout.isCompact ? styles.stack : styles.row,
      ]}
    >
      <View style={styles.main}>
        <Skeleton
          width="100%"
          height={layout.carouselHeight}
          borderRadius={16}
        />
        <View style={styles.headerBlock}>
          <Skeleton width="50%" height={32} borderRadius={4} />
          <Skeleton width="70%" height={18} borderRadius={4} />
        </View>
        <Skeleton width="100%" height={72} borderRadius={4} />
        <View style={styles.aboutBlock}>
          <Skeleton width="40%" height={24} borderRadius={4} />
          <Skeleton width={120} height={48} borderRadius={12} />
          <View style={styles.tagsRow}>
            <Skeleton width={64} height={28} borderRadius={8} />
            <Skeleton width={80} height={28} borderRadius={8} />
            <Skeleton width={88} height={28} borderRadius={8} />
          </View>
        </View>
      </View>

      <View style={[styles.sidebar, layout.isCompact && styles.sidebarCompact]}>
        <Skeleton width="60%" height={24} borderRadius={4} />
        {Array.from({ length: exploreCount }, (_, index) => (
          <GuestPropertyCardSkeleton
            key={`guest-explore-skel-${index}`}
            fullWidth
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  stack: {
    flexDirection: 'column',
    gap: 24,
  },
  main: {
    flexGrow: 729,
    flexShrink: 1,
    flexBasis: 0,
    gap: 20,
    minWidth: 0,
    maxWidth: '100%',
  },
  headerBlock: {
    gap: 8,
    width: '100%',
  },
  aboutBlock: {
    gap: 16,
    marginTop: 8,
    width: '100%',
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  sidebar: {
    flexGrow: 442,
    flexShrink: 1,
    flexBasis: 0,
    gap: 16,
    minWidth: 0,
    maxWidth: '100%',
  },
  sidebarCompact: {
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    width: '100%',
  },
});
